import { AuthenticatedGuard } from "@/components/authenticated-guard";
import {
  TitleRequestInfoTile,
  TitleRequestPropertyTile,
} from "@/components/non_registrant/title-request-details-sheet";
import PageHeader from "@/components/page-header";
import { CancelTitleRequestButton } from "@/components/title/cancel-title-request-button";
import { TitleRequestComments } from "@/components/title/title-request-comments";
import { TitleRequestStatusBadge } from "@/components/title/title-request-status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import { titleAuthConfigured } from "@/lib/title-auth";
import { getTitleLoginConnection } from "@/lib/title-login-connection";
import {
  type TitleRequestDetail,
  addTitleRequestComment,
  cancelMyTitleRequest,
  downloadTitleRequestAttachment,
  getMyTitleRequest,
  requestFurtherTitleReview,
  titleRequestActiveLabel,
} from "@/lib/title-request-api";
import { subscribeTitleRequestChanges } from "@/lib/title-request-live-refresh";
import { useAuth0 } from "@auth0/auth0-react";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

function RequestDetail({ requestId }: { requestId: string }) {
  const { getAccessTokenSilently } = useAuth0();
  const [detail, setDetail] = useState<TitleRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [furtherError, setFurtherError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [requestingFurther, setRequestingFurther] = useState(false);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return getMyTitleRequest(token, requestId);
  }, [getAccessTokenSilently, requestId]);

  useEffect(() => {
    let cancelled = false;
    void load()
      .then((result) => {
        if (!cancelled) {
          setDetail(result);
          setError(null);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "Could not load");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = subscribeTitleRequestChanges((events) => {
      if (!events.some((event) => event.request_id === requestId)) return;
      void load()
        .then((result) => {
          if (!cancelled) {
            setDetail(result);
            setError(null);
          }
        })
        .catch(() => undefined);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [load, requestId]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }
  if (!detail) {
    return <LoadingScreen message="Loading title request…" />;
  }

  const property = [detail.address1, detail.city, detail.state, detail.zipcode]
    .filter(Boolean)
    .join(", ");

  const showActions =
    detail.status === "TODO" || detail.status === "PROPERTY_NON_COMPLIANT";

  return (
    <div className="flex w-full min-w-[375px] flex-col gap-6">
      <PageHeader
        title={property || "Title request"}
        subtitle="Submitted title request details and correspondence."
        action={
          <div className="flex gap-2">
            <TitleRequestStatusBadge
              status={detail.status}
              className="text-sm"
            />
            <Badge variant="outline" className="text-sm">
              {titleRequestActiveLabel(detail.is_active)}
            </Badge>
          </div>
        }
      />
      <div className="@container">
        <div className="flex flex-col gap-6 @md:grid @md:grid-cols-3 @md:items-start">
          <div className="contents @md:col-span-2 @md:block @md:space-y-6">
            <TitleRequestPropertyTile
              detail={detail}
              className="order-1 @md:order-none"
            />
            <Card className="order-4 gap-4 @md:order-none">
              <CardHeader>
                <CardTitle>Comments</CardTitle>
              </CardHeader>
              <CardContent>
                <TitleRequestComments
                  comments={detail.comments ?? []}
                  onSubmit={async (comment) => {
                    const token = await getAccessTokenSilently();
                    const added = await addTitleRequestComment(
                      token,
                      detail.id,
                      comment,
                      false,
                    );
                    setDetail((current) =>
                      current
                        ? {
                            ...current,
                            status:
                              current.status === "REQUIRES_FURTHER_INFO"
                                ? "TODO"
                                : current.status,
                            comments: [...(current.comments ?? []), added],
                          }
                        : current,
                    );
                  }}
                />
              </CardContent>
            </Card>
            <Card className="order-5 gap-4 @md:order-none">
              <CardHeader>
                <CardTitle>Correspondence</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {detail.emails.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Hera has not sent a response yet.
                  </p>
                ) : null}
                {detail.emails.map((email) => (
                  <article key={email.id} className="rounded-md border p-4">
                    <p className="font-medium">{email.subject}</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">
                      {email.text_body}
                    </p>
                    {email.attachments.map((attachment) => (
                      <Button
                        key={attachment.id}
                        type="button"
                        variant="link"
                        className="mt-2 px-0"
                        onClick={() => {
                          void (async () => {
                            const token = await getAccessTokenSilently();
                            await downloadTitleRequestAttachment(
                              token,
                              detail.id,
                              attachment.id,
                              attachment.filename,
                              false,
                            );
                          })();
                        }}
                      >
                        {attachment.filename}
                      </Button>
                    ))}
                  </article>
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="contents @md:block @md:space-y-6">
            {showActions ? (
              <Card className="order-2 gap-4 @md:order-none">
                <CardHeader>
                  <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 text-sm">
                  {detail.status === "TODO" ? (
                    <div className="space-y-3">
                      <p className="text-muted-foreground">
                        Cancel this request if you no longer need a title
                        response. It will be marked Cancelled and inactive.
                      </p>
                      {cancelError ? (
                        <p className="text-destructive">{cancelError}</p>
                      ) : null}
                      <CancelTitleRequestButton
                        onConfirm={async () => {
                          setCancelError(null);
                          try {
                            const token = await getAccessTokenSilently();
                            setDetail(
                              await cancelMyTitleRequest(token, detail.id),
                            );
                          } catch (caught) {
                            setCancelError(
                              caught instanceof Error
                                ? caught.message
                                : "The request was not cancelled",
                            );
                            throw caught instanceof Error
                              ? caught
                              : new Error("The request was not cancelled");
                          }
                        }}
                      />
                    </div>
                  ) : null}
                  {detail.status === "PROPERTY_NON_COMPLIANT" ? (
                    <div className="space-y-3">
                      <p className="text-muted-foreground">
                        {detail.jurisdiction_name?.trim() ||
                          detail.tiger_name?.trim() ||
                          "The jurisdiction"}{" "}
                        has an active obligation on this property. After it is
                        resolved, raise a followup title request. The request
                        returns to Pending so Hera can review it again.
                      </p>
                      {furtherError ? (
                        <p className="text-destructive">{furtherError}</p>
                      ) : null}
                      <Button
                        type="button"
                        disabled={requestingFurther}
                        onClick={() => {
                          setRequestingFurther(true);
                          setFurtherError(null);
                          void (async () => {
                            try {
                              const token = await getAccessTokenSilently();
                              setDetail(
                                await requestFurtherTitleReview(
                                  token,
                                  detail.id,
                                ),
                              );
                            } catch (caught) {
                              setFurtherError(
                                caught instanceof Error
                                  ? caught.message
                                  : "The further title request was not saved",
                              );
                            } finally {
                              setRequestingFurther(false);
                            }
                          })();
                        }}
                      >
                        Raise Followup Title Request
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}
            <TitleRequestInfoTile
              detail={detail}
              className="order-3 @md:order-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/title/requests_/$requestId")({
  component: function TitleRequestDetailPage() {
    const { requestId } = Route.useParams();
    if (!titleAuthConfigured()) {
      return <p>Log in is not configured for title requests.</p>;
    }
    return (
      <AuthenticatedGuard
        allowedUserTypes={[UserType.TITLE_REQUESTOR, UserType.OPERATOR]}
        loginConnection={getTitleLoginConnection()}
      >
        <RequestDetail requestId={requestId} />
      </AuthenticatedGuard>
    );
  },
});
